import type { PGlite, QueryOptions, Results } from "@electric-sql/pglite";

import { createLogger } from "@/logger";

const log = createLogger("db");

const TRANSIENT_PORTAL_ERROR = "cannot drop active portal";
const RETRY_DELAY_MS = 25;
const MAX_RETRIES = 2;

function isTransientPortalError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if (error.message?.includes(TRANSIENT_PORTAL_ERROR)) return true;
  // DrizzleQueryError wraps the original PGlite error; walk the cause chain so we still
  // recognise the transient portal failure when it surfaces as `Failed query: ...`.
  const cause = (error as { cause?: unknown }).cause;
  return cause !== undefined && isTransientPortalError(cause);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function runWithRetry<T>(operation: () => Promise<T>, context: { method: string; sql?: string }): Promise<T> {
  let lastError: unknown;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      if (!isTransientPortalError(error) || attempt === MAX_RETRIES) {
        throw error;
      }

      log.warn("[clientWithRetry] Retrying after transient PGlite portal error", {
        method: context.method,
        sql: context.sql,
        attempt: attempt + 1,
        maxRetries: MAX_RETRIES,
      });

      await delay(RETRY_DELAY_MS * (attempt + 1));
    }
  }

  throw lastError;
}

/**
 * Wraps PGlite's `query` and `exec` methods so transient `cannot drop active portal` errors
 * — emitted when a new statement reaches the single-connection PGlite worker before the
 * prior portal has been deallocated — are retried with a short backoff before surfacing to
 * Drizzle. The error is benign (the prior portal releases on the next tick) but is fatal to
 * the affected query when it does propagate.
 *
 * The cluster (SVS-2397..2406, WEB-2006) appears across both bulk insert paths
 * (`feed_preference`, `audio_encoding`, `feed`, ...) and read paths
 * (`fetchChannelList` SELECT in WEB-2006). The PGlite 0.4.x upgrade in SVS-2503 reduced
 * the rate but did not eliminate the failure mode, so this client-layer retry is the
 * minimum-risk recovery.
 *
 * Installed against the raw PGlite client before `QueryMonitor` so each retry is captured
 * inside the existing Postgres timing spans.
 */
export function installPortalRetry(client: PGlite): void {
  if (!client || typeof client.query !== "function") {
    return;
  }

  const originalQuery = client.query;
  const originalExec = client.exec;

  client.query = async <T>(sql: string, params?: unknown[], options?: QueryOptions): Promise<Results<T>> =>
    runWithRetry<Results<T>>(() => originalQuery.call(client, sql, params, options) as Promise<Results<T>>, {
      method: "query",
      sql,
    });

  client.exec = async (sql: string, options?: QueryOptions) =>
    runWithRetry(() => originalExec.call(client, sql, options), { method: "exec", sql });
}
